/**
 * No-op shim for `@sentry/browser` and `@sentry/react`.
 *
 * Wired in via `config.resolve.alias` in webpack.config.js so every
 * `import * as Sentry from '@sentry/browser'` (and `@sentry/react`) in the
 * codebase resolves to this stub. We disable Sentry entirely in the extension
 * build to (a) drop the upstream `lazyLoadIntegration.js` which fetches
 * integration bundles from `https://browser.sentry-cdn.com` at runtime —
 * Chrome MV3 review flags that as remote hosted code — and (b) shrink the
 * shipped bundle by stripping all Sentry SDK code.
 *
 * All exports below match the names the codebase calls. Add more if a future
 * call site complains about an undefined export.
 */
'use strict'

const noop = () => {}
const asyncNoop = () => Promise.resolve()

function withScope(fn) {
  return fn({
    setTag: noop,
    setTags: noop,
    setExtra: noop,
    setExtras: noop,
    setLevel: noop,
    setUser: noop,
    setContext: noop,
    setFingerprint: noop
  })
}

function ErrorBoundary(props) {
  return props && props.children ? props.children : null
}

const integrationNoop = (name) => () => ({ name: name || 'noop', setupOnce: noop, setup: noop })

const stub = {
  init: noop,
  captureException: noop,
  captureMessage: noop,
  captureEvent: noop,
  withScope,
  configureScope: (fn) => fn({ setTag: noop, setExtra: noop, setUser: noop, setContext: noop }),
  getCurrentScope: () => ({
    setTag: noop,
    setExtra: noop,
    setUser: noop,
    setContext: noop,
    setLevel: noop,
    clear: noop
  }),
  getActiveSpan: () => undefined,
  startSpan: (_, fn) => (typeof fn === 'function' ? fn({}) : undefined),
  startInactiveSpan: () => ({ end: noop, setAttribute: noop, setStatus: noop }),
  setExtra: noop,
  setExtras: noop,
  setTag: noop,
  setTags: noop,
  setUser: noop,
  setContext: noop,
  addBreadcrumb: noop,
  flush: asyncNoop,
  close: asyncNoop,
  // Integrations
  breadcrumbsIntegration: integrationNoop('Breadcrumbs'),
  extraErrorDataIntegration: integrationNoop('ExtraErrorData'),
  dedupeIntegration: integrationNoop('Dedupe'),
  httpClientIntegration: integrationNoop('HttpClient'),
  contextLinesIntegration: integrationNoop('ContextLines'),
  linkedErrorsIntegration: integrationNoop('LinkedErrors'),
  globalHandlersIntegration: integrationNoop('GlobalHandlers'),
  browserApiErrorsIntegration: integrationNoop('BrowserApiErrors'),
  httpContextIntegration: integrationNoop('HttpContext'),
  lazyLoadIntegration: asyncNoop,
  replayIntegration: integrationNoop('Replay'),
  feedbackIntegration: integrationNoop('Feedback'),
  browserProfilingIntegration: integrationNoop('BrowserProfiling'),
  browserTracingIntegration: integrationNoop('BrowserTracing'),
  reactRouterV6BrowserTracingIntegration: integrationNoop('ReactRouterV6BrowserTracing'),
  // React-specific
  ErrorBoundary,
  withErrorBoundary: (Component) => Component,
  Profiler: ({ children }) => children,
  withProfiler: (Component) => Component,
  reactErrorHandler: () => noop,
  // Router wrappers (used by legends) — identity passthrough of the wrapped
  // createBrowserRouter so routing keeps working with Sentry stubbed out.
  wrapCreateBrowserRouterV6: (fn) => fn,
  wrapCreateBrowserRouterV7: (fn) => fn,
  wrapCreateMemoryRouterV6: (fn) => fn
}

module.exports = stub
module.exports.default = stub
